import _ from 'lodash'

import Api from '@/misc/api'
import { getFullname } from '@/misc/PersonHelper'
import { refreshActs, refreshGroupingResult } from '@/misc/stay/mco/helper'
import { simpleParseStay } from '@/misc/stay/rawStayMapper'
import { prefetchMcoStayDocuments } from '@/misc/stayDocuments'
import { refreshDisplayedMcoStay } from '@/misc/stayDocumentsMerge'
import { prefetchMcoStayFalseJustifications } from '@/misc/stayFalseJustifications'
import { fetchGroupedSuggestionJustifications, prefetchMcoKeywordJustifications, prefetchMcoStayJustifications } from '@/misc/stayJustifications'
import { prefetchMcoStaySuggestions } from '@/misc/staySuggestions'
import { convertEvaluationFeedbackOut, convertOwnEvaluationFeedbackIn } from '@/stay-displayer/store/dto'

const suggestedDasDisplayMode = sessionStorage.getItem('suggestedDasDisplayMode') ?? 'list'

const patchEvaluations = (displayedStay, evaluationId, patch) => {
  Object.values(displayedStay.suggestionJustifications ?? {}).forEach((justification) => {
    justification.evaluations.filter((evaluation) => evaluation.id === evaluationId).forEach(patch)
  })
}

const stayProcess = (stay) => {
  return {
    ...stay,
  }
}

const updateFalseJustificationChunks = (displayedStay, reportId, healthEntryId, hintStart, hintEnd, isCreation) => {
  let docType, docId
  if (reportId) {
    docType = 'reports'
    docId = reportId
  } else if (healthEntryId) {
    docType = 'textualHealthEntries'
    docId = healthEntryId
  }

  // Update the backend-provided chunks (see stayDocuments.ts::overrideStayChunks) in place, so the
  // flag/unflag is reflected immediately without waiting for a full stay refetch.
  _.forEach(displayedStay[docType], (item) => {
    if (item.id === docId) {
      _.forEach(item.chunks, (c) => {
        if (c.chunkStart >= hintStart && c.chunkEnd <= hintEnd) {
          c.isFalseJustification = isCreation
        }
      })
    }
  })
}

export default {
  state: {
    updateSelfRequest: { fetching: false, error: null, ok: false },
    originalStay: null, // This is the stay as it's returned from the API
    displayedStay: null, // This is the stay after the calculations (hints, dates loaded with dayjs, etc...)
    // True while refreshDisplayedMcoStay (documents/justifications/suggestions fetch + merge) is in
    // flight for the currently displayed stay — not part of the stay itself, so it isn't carried on
    // displayedStay.
    isRefreshingStayContent: false,
    mainContent: null, // as HealthDataDocument|null
    diagnosisSearchResults: {
      DP: [],
      DR: [],
      DAS: [],
      FPP: [],
      MMP: [],
      AE: [],
    },
    scrollTarget: null, // as { docId: number, offset: number }|null ; non-null means "scroll to this justification when the document opens"
    suggestedDasDisplayMode,
    rulesAutomationPreview: null,
    getRulesAutomationPreviewRequest: { fetching: false, error: null, ok: false },
    aiAutomationPreview: null,
    getAiAutomationPreviewRequest: { fetching: false, error: null, ok: false },
    getStayRequest: { fetching: false, error: null, ok: false },
    addDiagnosisRequest: {
      DP: { fetching: false, error: null, ok: false },
      DR: { fetching: false, error: null, ok: false },
      DAS: { fetching: false, error: null, ok: false },
    },
    keywordList: [],
    switchDiagnosisRequest: { fetching: false, error: null, ok: false },
    removeDiagnosisRequest: { fetching: false, error: null, ok: false },
    toggleInitialDasRequest:{ fetching: false, error: null, ok: false },
    togglePredictedDasRequest:{ fetching: false, error: null, ok: false },
    searchDiagnosesRequest: {
      DP: { fetching: false, error: null, ok: false },
      DR: { fetching: false, error: null, ok: false },
      DAS: { fetching: false, error: null, ok: false },
      FPP: { fetching: false, error: null, ok: false },
      MMP: { fetching: false, error: null, ok: false },
      AE: { fetching: false, error: null, ok: false },
    },
    // Fetched lazily (on switch to card display mode, not alongside the stay) since only the card
    // view needs the suggestions grouped by shared justification.
    groupedSuggestionJustifications: null,
    groupedSuggestionJustificationsStayId: null,
    getGroupedSuggestionJustificationsRequest: { fetching: false, error: null, ok: false },
    searchStayRequest: { fetching: false, error: null, ok: false },
    searchStayResults: [],

    updateStayStatusRequest: { fetching: false, error: null, ok: false },
    syncStayToExternalConnectorRequest: { fetching: false, error: null, ok: false },

    createFalseJustificationRequest: { fetching: false, error: null, ok: false },
    deleteFalseJustificationRequest: { fetching: false, error: null, ok: false },

    disableAutomationRequest: { fetching: false, error: null, ok: false },
  },
  mutations: {
    updateDisplayedStay(state, stay) {
      state.displayedStay = stay
    },
    startRefreshingStayContent(state) {
      state.isRefreshingStayContent = true
    },
    endRefreshingStayContent(state) {
      state.isRefreshingStayContent = false
    },
    setScrollTarget(state, scrollTarget) {
      state.scrollTarget = scrollTarget
    },
    setMainContent(state, mainContent) {
      state.mainContent = mainContent
    },
    getStayStart(state, { stayId, idType }) {
      state.getStayRequest = { fetching: true, error: null, ok: false, stayId, idType }
    },
    getStaySuccess(state, { stay }) {
      state.getStayRequest.fetching = false
      state.getStayRequest.ok = true
      state.originalStay = { ...stay }
      state.displayedStay = simpleParseStay(stay)
      state.rulesAutomationPreview = null
      state.aiAutomationPreview = null
      state.groupedSuggestionJustifications = null
      state.groupedSuggestionJustificationsStayId = null
    },
    getStayError(state, res) {
      state.getStayRequest.fetching = false
      state.getStayRequest.error = res
      state.displayedStay = null
    },
    getRulesAutomationPreviewStart(state) {
      state.getRulesAutomationPreviewRequest = { fetching: true, error: null, ok: false }
    },
    getRulesAutomationPreviewSuccess(state, preview) {
      state.getRulesAutomationPreviewRequest = { fetching: false, error: null, ok: true }
      state.rulesAutomationPreview = preview
    },
    getRulesAutomationPreviewError(state, res) {
      state.getRulesAutomationPreviewRequest = { fetching: false, error: res, ok: false }
      state.rulesAutomationPreview = null
    },
    resetRulesAutomationPreview(state) {
      state.rulesAutomationPreview = null
    },
    getAiAutomationPreviewStart(state) {
      state.getAiAutomationPreviewRequest = { fetching: true, error: null, ok: false }
    },
    getAiAutomationPreviewSuccess(state, preview) {
      state.getAiAutomationPreviewRequest = { fetching: false, error: null, ok: true }
      state.aiAutomationPreview = preview
    },
    getAiAutomationPreviewError(state, res) {
      state.getAiAutomationPreviewRequest = { fetching: false, error: res, ok: false }
      state.aiAutomationPreview = null
    },
    resetAiAutomationPreview(state) {
      state.aiAutomationPreview = null
    },

    recomputeDisplayedStay(state) {
      if (state.originalStay) {
        // FIXME this is quite ugly since there are some on-the-fly edits to the stay (falseJustifications for instance)
        state.displayedStay = simpleParseStay(state.originalStay)
      }
    },

    addDiagnosisStart(state, diagnosisType) {
      state.addDiagnosisRequest[diagnosisType] = { fetching: true, error: null, ok: false }
    },
    addDiagnosisSuccess(state, { stay, diagnosisType }) {
      state.addDiagnosisRequest[diagnosisType] = { fetching: false, error: null, ok: true }
      state.originalStay = { ...stay }
      state.displayedStay = simpleParseStay(stay)
    },
    addDiagnosisError(state, res) {
      state.addDiagnosisRequest[res.diagnosisType] = { fetching: false, error: res, ok: false }
    },
    resetAddDiagnosis(state, diagnosisType) {
      state.addDiagnosisRequest[diagnosisType] = { fetching: false, error: null, ok: false }
    },
    commentStayStart(state) {
      state.commentStayRequest = { fetching: true, error: null, ok: false }
    },
    commentStayError(state, res) {
      state.commentStayRequest = { fetching: false, error: res.message, ok: false }
    },
    switchDiagnosisStart(state) {
      state.switchDiagnosisRequest = { fetching: true, error: null, ok: false }
    },
    switchDiagnosisSuccess(state) {
      state.switchDiagnosisRequest = { fetching: false, error: null, ok: true }
    },
    switchDiagnosisError(state, res) {
      state.switchDiagnosisRequest = { fetching: false, error: res.message, ok: false }
    },

    // remove DP mutations
    removeDPStart(state) {
      state.removeDPRequest = { fetching: true, error: null, ok: false }
    },
    removeDPSuccess(state, { stay }) {
      state.removeDPRequest = { fetching: false, error: null, ok: true }
      state.originalStay = { ...stay }
      state.displayedStay = simpleParseStay(stay)
    },
    removeDPError(state, res) {
      state.removeDPRequest = { fetching: false, error: res, ok: false }
    },
    // removeDR mutations
    removeDRStart(state) {
      state.removeDRRequest = { fetching: true, error: null, ok: false }
    },
    removeDRSuccess(state, { stay }) {
      state.removeDRRequest = { fetching: false, error: null, ok: true }
      state.originalStay = { ...stay }
      state.displayedStay = simpleParseStay(stay)
    },
    removeDRError(state, res) {
      state.removeDRRequest = { fetching: false, error: res, ok: false }
    },
    // removeDAS mutations
    removeDASStart(state) {
      state.removeDASRequest = { fetching: true, error: null, ok: false }
    },
    removeDASSuccess(state, { stay }) {
      state.removeDASRequest = { fetching: false, error: null, ok: true }
      state.originalStay = { ...stay }
      state.displayedStay = simpleParseStay(stay)
    },
    removeDASError(state, res) {
      state.removeDASRequest = { fetching: false, error: res, ok: false }
    },

    updateMedicalActsSuccess(state, updatedStay) {
      refreshActs(state.originalStay, updatedStay)
      refreshGroupingResult(state.originalStay, updatedStay)
      // caution: for now justifications are not handled
      refreshActs(state.displayedStay, updatedStay)
      refreshGroupingResult(state.displayedStay, updatedStay)
    },

    searchStayStart(state) {
      state.searchStayRequest = { fetching: true, error: null, ok: false }
    },
    searchStaySuccess(state, stays) {
      state.searchStayRequest = { fetching: false, error: false, ok: true }
      state.searchStayResults = stays
    },
    searchStayError(state, res) {
      state.searchStayRequest.fetching = false
      state.searchStayRequest.error = res
    },
    clearSearchStay(state) {
      state.searchStayResults = []
    },

    searchDiagnosesStart(state, diagnosisType) {
      state.searchDiagnosesRequest[diagnosisType] = { fetching: true, error: null, ok: false }
    },
    searchDiagnosesSuccess(state, { diagnosisType, diagnoses }) {
      state.searchDiagnosesRequest[diagnosisType].fetching = false
      state.searchDiagnosesRequest[diagnosisType].ok = true
      state.diagnosisSearchResults[diagnosisType] = diagnoses
    },
    searchDiagnosesError(state, res) {
      state.searchDiagnosesRequest[res.diagnosisType].fetching = false
      state.searchDiagnosesRequest[res.diagnosisType].error = res
    },
    clearSearchDiagnoses(state, diagnosisType) {
      state.diagnosisSearchResults[diagnosisType] = []
    },
    commentStaySuccess(state, stay) {
      state.commentStayRequest = { fetching: false, ok: true }
      state.displayedStay.comment = stay.comment
    },
    updateStayStatusStart(state) {
      state.updateStayStatusRequest = { fetching: true, error: null, ok: false }
    },
    updateStayStatusSuccess(state, { stay }) {
      state.updateStayStatusRequest.fetching = false
      state.updateStayStatusRequest.ok = true
      state.originalStay = { ...stay }
      state.displayedStay = simpleParseStay(stay)
    },
    updateStayStatusError(state, res) {
      state.updateStayStatusRequest.fetching = false
      state.updateStayStatusRequest.error = res
    },
    createFalseJustificationsStart(state) {
      state.createFalseJustificationRequest = { fetching: true, error: null, ok: false }
    },
    createFalseJustificationsSuccess(state, falseJustification) {
      state.createFalseJustificationRequest.fetching = false
      state.createFalseJustificationRequest.ok = true

      const reportId = falseJustification.report ? falseJustification.report.id : null
      const healthEntryId = falseJustification.textualHealthEntry ? falseJustification.textualHealthEntry.id : null

      state.displayedStay.falseJustifications = [
        ...(state.displayedStay.falseJustifications ?? []),
        {
          id: falseJustification.id,
          type: falseJustification.type,
          hintStart: falseJustification.hintStart,
          hintEnd: falseJustification.hintEnd,
          documentType: reportId ? 'report' : 'textualHealthEntry',
          documentId: reportId ?? healthEntryId,
        },
      ]

      updateFalseJustificationChunks(state.displayedStay, reportId, healthEntryId, falseJustification.hintStart, falseJustification.hintEnd, true)
    },
    createFalseJustificationsError(state, res) {
      state.createFalseJustificationRequest.fetching = false
      state.createFalseJustificationRequest.error = res
    },
    deleteFalseJustificationsStart(state) {
      state.createFalseJustificationRequest = { fetching: true, error: null, ok: false }
    },
    deleteFalseJustificationsSuccess(state, { reportId, healthEntryId, justificationId }) {
      state.createFalseJustificationRequest.fetching = false
      state.createFalseJustificationRequest.ok = true

      const target = _.find(state.displayedStay.falseJustifications, (j) => j.id === justificationId)
      if (target) {
        state.displayedStay.falseJustifications = _.filter(state.displayedStay.falseJustifications, (j) => j.id !== justificationId)
        updateFalseJustificationChunks(state.displayedStay, reportId, healthEntryId, target.hintStart, target.hintEnd, false)
      }
    },
    deleteFalseJustificationsError(state, res) {
      state.createFalseJustificationRequest.fetching = false
      state.createFalseJustificationRequest.error = res
    },
    // The same evaluation can show up under several justifications (wildcard parent and children).
    setOwnEvaluationFeedback(state, { evaluationId, feedback }) {
      patchEvaluations(state.displayedStay, evaluationId, (evaluation) => {
        evaluation.feedbacks = [...evaluation.feedbacks.filter((f) => !f.isMine), ...(feedback ? [feedback] : [])]
      })
    },
    disableAutomationStart(state) {
      state.disableAutomationRequest = { fetching: true, error: null, ok: false }
    },
    disableAutomationSuccess(state, { stay }) {
      state.disableAutomationRequest = { fetching: false, error: null, ok: true }
      state.originalStay = { ...stay }
      state.displayedStay = simpleParseStay(stay)
    },
    disableAutomationError(state, res) {
      state.disableAutomationRequest = { fetching: false, error: res, ok: false }
    },
    setSuggestedDasDisplayMode(state, mode) {
      state.suggestedDasDisplayMode = mode
      sessionStorage.setItem('suggestedDasDisplayMode', mode)
    },
    getGroupedSuggestionJustificationsStart(state) {
      state.getGroupedSuggestionJustificationsRequest = { fetching: true, error: null, ok: false }
    },
    getGroupedSuggestionJustificationsSuccess(state, { stayId, sancareSuggestions }) {
      state.getGroupedSuggestionJustificationsRequest = { fetching: false, error: null, ok: true }
      state.groupedSuggestionJustifications = sancareSuggestions
      state.groupedSuggestionJustificationsStayId = stayId
    },
    getGroupedSuggestionJustificationsError(state, res) {
      state.getGroupedSuggestionJustificationsRequest = { fetching: false, error: res, ok: false }
    },
    syncStayToExternalConnectorStart(state) {
      state.syncStayToExternalConnectorRequest = { fetching: true, error: null, ok: false }
    },
    syncStayToExternalConnectorSuccess(state) {
      state.syncStayToExternalConnectorRequest = { fetching: false, error: null, ok: true }
    },
    syncStayToExternalConnectorError(state, res) {
      state.syncStayToExternalConnectorRequest = { fetching: false, error: res, ok: false }
    },
    syncStayToExternalConnectorReset(state) {
      state.syncStayToExternalConnectorRequest = { fetching: false, error: null, ok: false }
    },
  },
  actions: {
    getStay({ commit, state, rootState }, { stayId, idType }) {
      idType = _.isString(idType) && idType.length > 0 ? idType : 'legacy'
      if (state.getStayRequest.fetching && state.getStayRequest.stayId === stayId && state.getStayRequest.idType === idType) {
        // A request for this exact stay is already in flight — avoid firing the fetch twice.
        return Promise.resolve()
      }
      let documentsPromise = null
      let justificationsPromise = null
      let keywordJustificationsPromise = null
      let suggestionsPromise = null
      let falseJustificationsPromise = null
      return Api.get(
        {
          url: `/api/stays/${idType}/${stayId}`,
          startMut: () => {
            commit('getStayStart', { stayId, idType })
            // Fired alongside the stay request (not after it resolves) so both come back in parallel.
            documentsPromise = prefetchMcoStayDocuments(idType, stayId, rootState.stayList.currentSearch, commit, true)
            justificationsPromise = prefetchMcoStayJustifications(idType, stayId, commit, true)
            keywordJustificationsPromise = prefetchMcoKeywordJustifications(idType, stayId, rootState.stayList.currentSearch, commit)
            suggestionsPromise = prefetchMcoStaySuggestions(idType, stayId, commit, false, true)
            falseJustificationsPromise = prefetchMcoStayFalseJustifications(idType, stayId, commit)
          },
          successMut: async (stay) => {
            stay = stayProcess(stay)
            commit('getStaySuccess', { stay })
            await refreshDisplayedMcoStay(stay, commit, rootState.stayList.currentSearch, rootState.settings.appSettings, null, documentsPromise, justificationsPromise, keywordJustificationsPromise, suggestionsPromise, falseJustificationsPromise)
          },
          errorMut: 'getStayError',
        },
        commit
      )
    },
    getRulesAutomationPreview({ commit }, { stayId, automationGroupId }) {
      return Api.get(
        {
          url: `/api/stays/${stayId}/rules-automation-preview/${automationGroupId}`,
          startMut: 'getRulesAutomationPreviewStart',
          successMut: 'getRulesAutomationPreviewSuccess',
          errorMut: 'getRulesAutomationPreviewError',
        },
        commit
      )
    },
    getAiAutomationPreview({ commit }, { stayId }) {
      return Api.get(
        {
          url: `/api/stays/${stayId}/ai-automation-preview`,
          startMut: 'getAiAutomationPreviewStart',
          successMut: 'getAiAutomationPreviewSuccess',
          errorMut: 'getAiAutomationPreviewError',
        },
        commit
      )
    },
    async recomputeDisplayedStay({ commit, rootState, state }) {
      const previousStay = state.displayedStay
      commit('recomputeDisplayedStay')
      await refreshDisplayedMcoStay(state.originalStay, commit, rootState.stayList.currentSearch, rootState.settings.appSettings, previousStay)
    },
    async getGroupedSuggestionJustifications({ commit, state }) {
      const stayId = state.originalStay?.id
      if (!stayId
        || state.groupedSuggestionJustificationsStayId === stayId
        || state.getGroupedSuggestionJustificationsRequest.fetching
      ) {
        return
      }

      commit('getGroupedSuggestionJustificationsStart')
      try {
        const response = await fetchGroupedSuggestionJustifications(String(stayId), commit)
        commit('getGroupedSuggestionJustificationsSuccess', { stayId, sancareSuggestions: response.sancareSuggestions })
      } catch (err) {
        commit('getGroupedSuggestionJustificationsError', err)
      }
    },
    resetAddDiagnosis({ commit }, diagnosisType) {
      commit('resetAddDiagnosis', diagnosisType)
    },
    addDiagnosis({ commit, rootState, state }, { diagnosisType, rumId, reference, justifications }) {
      return Api.post(
        {
          url: '/api/manual-diagnosis/standard-mode',
          body: { rumId: rumId, diagnosisType, diagnosis: reference, justifications },
          startMut: () => commit('addDiagnosisStart', diagnosisType),
          successMut: async (stay) => {
            const previousStay = state.displayedStay
            stay = stayProcess(stay)
            commit('addDiagnosisSuccess', { stay, diagnosisType })
            await refreshDisplayedMcoStay(stay, commit, rootState.stayList.currentSearch, rootState.settings.appSettings, previousStay)
            commit('setStayListInitialized', false)
          },
          errorMut: (res) => commit('addDiagnosisError', { ...res, diagnosisType }),
        },
        commit
      )
    },
    commentStay({ commit }, { stayId, comment }) {
      return Api.post(
        {
          url: `/api/stays/comment/${stayId}`,
          body: { comment },
          startMut: 'commentStayStart',
          successMut: (stay) => {
            commit('commentStaySuccess', stay)
            commit('setStayListInitialized', false)
          },
          errorMut: 'commentStayError'
        },
        commit
      )
    },
    switchDiagnosis({ commit, rootState, state }, { rumId, origin, destination }) {
      return Api.post(
        {
          url: '/api/manual-diagnosis/switch-diagnosis',
          body: { rumId, origin, destination },
          startMut: () => commit('switchDiagnosisStart'),
          successMut: async (stay) => {
            const previousStay = state.displayedStay
            stay = stayProcess(stay)
            commit('switchDiagnosisSuccess')
            await refreshDisplayedMcoStay(stay, commit, rootState.stayList.currentSearch, rootState.settings.appSettings, previousStay)
            commit('setStayListInitialized', false)
          },
          errorMut: (res) => commit('switchDiagnosisError', res),
        },
        commit
      )
    },
    // TODO: refacto the various stay processing steps that occur in each of these removeDP/DR/DAS actions
    removeDP({ commit, rootState, state }, { rumId }) {
      return Api.del(
        {
          url: `/api/manual-diagnosis/dp/${rumId}`,
          startMut: 'removeDPStart',
          successMut: async (stay) => {
            const previousStay = state.displayedStay
            stay = stayProcess(stay)
            commit('removeDPSuccess', { stay })
            await refreshDisplayedMcoStay(stay, commit, rootState.stayList.currentSearch, rootState.settings.appSettings, previousStay)
            commit('setStayListInitialized', false)
          },
          errorMut: 'removeDPError',
        },
        commit
      )
    },
    removeDR({ commit, rootState, state }, { rumId }) {
      return Api.del(
        {
          url: `/api/manual-diagnosis/dr/${rumId}`,
          startMut: 'removeDRStart',
          successMut: async (stay) => {
            const previousStay = state.displayedStay
            stay = stayProcess(stay)
            commit('removeDRSuccess', { stay })
            await refreshDisplayedMcoStay(stay, commit, rootState.stayList.currentSearch, rootState.settings.appSettings, previousStay)
            commit('setStayListInitialized', false)
          },
          errorMut: 'removeDRError',
        },
        commit
      )
    },
    removeDAS({ commit, rootState, state }, { diagId }) {
      return Api.del(
        {
          url: `/api/manual-diagnosis/das/${diagId}`,
          startMut: 'removeDASStart',
          successMut: async (stay) => {
            const previousStay = state.displayedStay
            stay = stayProcess(stay)
            commit('removeDASSuccess', { stay })
            await refreshDisplayedMcoStay(stay, commit, rootState.stayList.currentSearch, rootState.settings.appSettings, previousStay)
            commit('setStayListInitialized', false)
          },
          errorMut: 'removeDASError',
        },
        commit
      )
    },
    searchDiagnoses({ commit }, { diagnosisType, contextDate, search, signal, searchByType = false }) {
      return Api.post(
        {
          url: '/api/stays/search-diagnosis',
          body: searchByType ? { search, diagnosisType, contextDate } : { search, contextDate },
          signal,
          startMut: () => commit('searchDiagnosesStart', diagnosisType),
          successMut: (diagnoses) => commit('searchDiagnosesSuccess', { diagnosisType, diagnoses }),
          errorMut: (res) => commit('searchDiagnosesError', { ...res, diagnosisType }),
        },
        commit
      )
    },
    searchStay({ commit }, { search, pmsiType }) {
      return Api.post(
        {
          url: `/api/stays/${pmsiType}/search-stay`,
          body: { search },
          startMut: 'searchStayStart',
          successMut: 'searchStaySuccess',
          errorMut: 'searchStayError',
        },
        commit
      )
    },
    updateStayStatus({ commit, rootState, state }, { stayId, status, mode, pmsiType, automationGroupId }) {
      return Api.post(
        {
          url: `/api/stays/${pmsiType}/update-status`,
          body: { stayId, status, mode, automationGroupId },
          startMut: 'updateStayStatusStart',
          successMut: async (stay) => {
            const previousStay = state.displayedStay
            stay = stayProcess(stay)
            commit('updateStayStatusSuccess', { stay })
            await refreshDisplayedMcoStay(stay, commit, rootState.stayList.currentSearch, rootState.settings.appSettings, previousStay)
            commit('setStayListInitialized', false)
          },
          errorMut: 'updateStayStatusError',
        },
        commit
      )
    },
    // Used by backend-driven popins and the raw document view alike (ApiJustificationSummary.vue and
    // ReportDisplayer.vue/TextualHealthEntries.vue via flag-justification), where the label
    // (type+reference) is already known unambiguously — the backend resolves origin/searches/diagnoses
    // itself (ResolveFalseJustificationOriginUseCase) instead of the frontend guessing it.
    createFalseJustificationForSuggestion({ commit, state }, { errorType, docType, docId, hintStart, hintEnd, labelType, labelReference }) {
      return Api.post(
        {
          url: `/api/mco/stay/${state.displayedStay.id}/false-justification`,
          body: { errorType, docType, docId, hintStart, hintEnd, labelType, labelReference },
          startMut: 'createFalseJustificationsStart',
          successMut: 'createFalseJustificationsSuccess',
          errorMut: (res) => commit('createFalseJustificationsError', { ...res }),
        },
        commit
      )
    },
    // Both resolve to whether the call succeeded: Api never rejects nor returns the outcome, a failure
    // only shows a toast (the form stays open then, keeping what the user typed).
    async giveEvaluationFeedback({ commit, state }, { evaluationId, verdict, comment }) {
      let isSuccess = false
      await Api.post(
        {
          url: `/api/mco/stay/${state.displayedStay.id}/evaluated-label/${evaluationId}/feedback`,
          body: convertEvaluationFeedbackOut({ verdict, comment }),
          successMut: (res) => {
            commit('setOwnEvaluationFeedback', { evaluationId, feedback: convertOwnEvaluationFeedbackIn(res) })
            isSuccess = true
          },
        },
        commit
      )
      return isSuccess
    },
    async removeEvaluationFeedback({ commit, state }, { evaluationId }) {
      let isSuccess = false
      await Api.del(
        {
          url: `/api/mco/stay/${state.displayedStay.id}/evaluated-label/${evaluationId}/feedback`,
          successMut: () => {
            commit('setOwnEvaluationFeedback', { evaluationId, feedback: null })
            isSuccess = true
          },
        },
        commit
      )
      return isSuccess
    },
    deleteFalseJustification({ commit }, { justificationId, reportId, healthEntryId }) {
      return Api.del(
        {
          url: `/api/false-justification/${justificationId}`,
          startMut: 'deleteFalseJustificationsStart',
          successMut: () => commit('deleteFalseJustificationsSuccess', { justificationId, reportId, healthEntryId }),
          errorMut: (res) => commit('deleteFalseJustificationsError', { ...res }),
        },
        commit
      )
    },
    disableAutomation({ commit, rootState, state }, { stayId }) {
      return Api.post(
        {
          url: '/api/manual-diagnosis/disable-automation',
          body: { stayId },
          startMut: 'disableAutomationStart',
          successMut: async (stay) => {
            const previousStay = state.displayedStay
            stay = stayProcess(stay)
            commit('disableAutomationSuccess', { stay })
            await refreshDisplayedMcoStay(stay, commit, rootState.stayList.currentSearch, rootState.settings.appSettings, previousStay)
          },
          errorMut: (res) => commit('disableAutomationError', { ...res }),
        },
        commit
      )
    },
    syncStayToExternalConnector({ commit }, { stayId }) {
      return Api.post(
        {
          url: `/api/stays/sync/${stayId}`,
          body: null,
          startMut: 'syncStayToExternalConnectorStart',
          successMut: () => {
            commit('syncStayToExternalConnectorSuccess')
            setTimeout(() => {
              commit('syncStayToExternalConnectorReset')
            }, 5000)
          },
          errorMut: (res) => {
            commit('syncStayToExternalConnectorError', res)
            setTimeout(() => {
              commit('syncStayToExternalConnectorReset')
            }, 5000)
          },
        },
        commit,
      )
    },
  },
  getters: {
    fullName: (state) => state.displayedStay?.patient ? getFullname(state.displayedStay.patient.lastName, state.displayedStay.patient.maidenName, state.displayedStay.patient.firstName) : ''
  }
}
